import { EventInput, ShiftInput } from '@amzn/red-velvet-api';
import { useUpdateEventDraft } from './draftEvent';
import { useCreateEvent } from './createEvent';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SaveStatus,
  SAVE_STATUS_IDLE,
  SAVE_STATUS_SAVING,
  SAVE_STATUS_SAVED,
  SAVE_STATUS_ERROR,
} from '../../utils/constants';
import { publishKatalMetric } from '../../components/katalAnalytics';

// Smithy @length limits for EventInput and ShiftInput fields
const MAX_TITLE = 255;
const MAX_DESCRIPTION = 65536;
const MAX_SHORT_DESCRIPTION = 100;
const MAX_ADDITIONAL_COORDINATORS = 5;
const MAX_SHIFT_NAME = 128;

interface UseAutoSaveEventDraftProps {
  eventId: React.MutableRefObject<string>;
  metricPrefix: string;
  setHasUnsavedChanges: (value: boolean) => void;
  onShiftIdsSynced?: (shiftIds: string[]) => void;
  onSaveError?: (error: Error) => void;
  onEventCreated?: () => void;
}

export function useAutoSaveEventDraft({
  eventId,
  metricPrefix,
  setHasUnsavedChanges,
  onShiftIdsSynced,
  onSaveError,
  onEventCreated,
}: UseAutoSaveEventDraftProps) {
  const { mutate: updateEventDraft } = useUpdateEventDraft();
  const { mutate: createEvent } = useCreateEvent();
  const isCreating = useRef<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>(SAVE_STATUS_IDLE);
  const [lastSavedTime, setLastSavedTime] = useState<Date | null>(null);

  // Pending saves buffered while the draft is being created
  const pendingEventSave = useRef<EventInput | null>(null);
  const pendingShiftSave = useRef<ShiftInput[] | null>(null);

  const markSaved = () => {
    setSaveStatus(SAVE_STATUS_SAVED);
    setLastSavedTime(new Date());
  };

  const saveDraft = (event?: EventInput, shifts?: ShiftInput[], onSuccessCallback?: () => void) => {
    if (eventId.current === '') return;
    setHasUnsavedChanges(true);
    setSaveStatus(SAVE_STATUS_SAVING);
    // Truncate fields that exceed Smithy limits to prevent 400s
    if (event) {
      event = { ...event };
      if (event.title === '') event.title = undefined;
      if (event.title) event.title = event.title.slice(0, MAX_TITLE);
      if (event.description) event.description = event.description.slice(0, MAX_DESCRIPTION);
      if (event.shortDescription)
        event.shortDescription = event.shortDescription.slice(0, MAX_SHORT_DESCRIPTION);
      if (event.timeZone === '') event.timeZone = undefined;
      if (event.additionalCoordinators)
        event.additionalCoordinators = event.additionalCoordinators.slice(
          0,
          MAX_ADDITIONAL_COORDINATORS
        );
    }
    if (shifts) {
      shifts = shifts.map((s) =>
        s.name && s.name.length > MAX_SHIFT_NAME
          ? { ...s, name: s.name.slice(0, MAX_SHIFT_NAME) }
          : s
      );
    }
    updateEventDraft(
      {
        eventId: eventId.current,
        ...(event && { event }),
        ...(shifts && { shifts }),
      },
      {
        onSuccess: (response) => {
          setHasUnsavedChanges(false);
          markSaved();
          if (response?.shiftIds?.length && onShiftIdsSynced) {
            onShiftIdsSynced(response.shiftIds);
          }
          onSuccessCallback?.();
        },
        onError: (error) => {
          publishKatalMetric(`${metricPrefix}_autoSaveError`, error.message || 'unknown');
          onSaveError?.(error);
          setSaveStatus(SAVE_STATUS_ERROR);
        },
      }
    );
  };

  // Save any changes that came in while the draft was being created
  const flushPendingSaves = () => {
    const pendingEvent = pendingEventSave.current;
    const pendingShifts = pendingShiftSave.current;
    pendingEventSave.current = null;
    pendingShiftSave.current = null;
    if (pendingEvent || pendingShifts) {
      saveDraft(pendingEvent ?? undefined, pendingShifts ?? undefined);
    }
  };

  const autoSaveHandler = (updatedEventDetails: EventInput) => {
    setHasUnsavedChanges(true);

    // An empty eventId means that this is the first time the event is being saved
    if (updatedEventDetails.title !== '' && eventId.current === '' && !isCreating.current) {
      isCreating.current = true;
      setSaveStatus(SAVE_STATUS_SAVING);
      createEvent(
        { title: updatedEventDetails.title as string },
        {
          onSuccess: (response) => {
            eventId.current = response.eventId;
            setHasUnsavedChanges(false);
            isCreating.current = false;
            onEventCreated?.();
            markSaved();

            // Update the url params to include the generated draft id
            const newParams = new URLSearchParams(searchParams);
            newParams.set('draftId', response.eventId);
            setSearchParams(newParams);

            flushPendingSaves();
          },
          onError: (error) => {
            isCreating.current = false;
            pendingEventSave.current = null;
            pendingShiftSave.current = null;
            publishKatalMetric(`${metricPrefix}_autoSaveError`, error.message || 'unknown');
            onSaveError?.(error);
            setSaveStatus(SAVE_STATUS_ERROR);
          },
        }
      );
    } else if (isCreating.current || eventId.current === '') {
      // Draft creation in progress or no title yet, then stash the latest fields
      pendingEventSave.current = updatedEventDetails;
    } else {
      saveDraft(updatedEventDetails);
    }
  };

  const autoSaveShiftsHandler = (shifts: ShiftInput[]) => {
    if (isCreating.current || eventId.current === '') {
      pendingShiftSave.current = shifts;
      return;
    }
    saveDraft(undefined, shifts);
  };

  return { autoSaveHandler, autoSaveShiftsHandler, saveDraft, saveStatus, lastSavedTime };
}
